import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import { cacheScope, clearCacheOnAuthFailure, confirmProjectDetail, invalidateCache, setLiveConfirmedProjects, writeCache } from "@/lib/ui-cache";
import { projectDetailPreview, projectPreview, type CachedProject } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";
import i18n from "@/lib/i18n";
import type {
  Project,
  ProjectCreateRequest,
  ProjectListResponse,
  ProjectUpdateRequest,
} from "@/lib/project-types";

const API_BASE = "/api/projects";
const PROJECTS_PAGE_SIZE = 50;

const jsonHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

export interface UseProjectsReturn {
  projects: Project[];
  loading: boolean;
  error: string | null;
  /** True after at least one successful project-list response. */
  loaded: boolean;
  /** Timestamp of the most recent confirmed list or local project change. */
  updatedAt: number | null;
  /** List-specific failures, excluding create/update/pin action errors. */
  listError: string | null;
  hasMore: boolean;
  nextCursor: string | null;
  list: (limit?: number, before?: string | null) => Promise<Project[] | null>;
  create: (input: ProjectCreateRequest) => Promise<Project | null>;
  get: (id: string) => Promise<Project | null>;
  update: (id: string, input: ProjectUpdateRequest) => Promise<Project | null>;
  archive: (id: string) => Promise<boolean>;
  pin: (id: string) => Promise<boolean>;
  unpin: (id: string) => Promise<boolean>;
  /** Apply a confirmed project change from another local Projects view. */
  upsert: (project: Project) => void;
  /** Remove a confirmed project change from another local Projects view. */
  remove: (id: string) => void;
  refresh: () => Promise<Project[] | null>;
  revalidateAccess: () => Promise<Project[] | null>;
}

export interface UseProjectsOptions {
  /** Set false when a caller resolves one project directly with get(). */
  autoLoad?: boolean;
}

/**
 * Manage the current user's projects with cursor-based pagination.
 *
 * A cursor is opaque to the client and uses the backend's `ISO-8601|uuid`
 * format, matching the sessions list API.
 */
export function useProjects(
  getToken: () => Promise<string>,
  { autoLoad = true }: UseProjectsOptions = {},
): UseProjectsReturn {
  const verifiedScope = useCacheScope();
  const previousScopeRef = useRef(verifiedScope);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const projectsLoadedRef = useRef(false);
  // A project returned by POST/PATCH is newer than any in-flight list request.
  // Keep it through the next list replacement until that response includes the
  // project itself. This is also what lets a separate sidebar hook apply a
  // window event immediately without a stale GET briefly removing the folder.
  const pendingProjectsRef = useRef<Project[]>([]);
  const pendingRemovedProjectIdsRef = useRef<Set<string>>(new Set());
  const refreshInFlightRef = useRef<Promise<Project[] | null> | null>(null);
  const listRequestIdRef = useRef(0);

  const upsert = useCallback((project: Project) => {
    pendingRemovedProjectIdsRef.current.delete(project.id);
    pendingProjectsRef.current = [
      project,
      ...pendingProjectsRef.current.filter((item) => item.id !== project.id),
    ];
    setProjects((current) => {
      const existingIndex = current.findIndex((item) => item.id === project.id);
      if (existingIndex === -1) return [project, ...current];
      const next = [...current];
      next[existingIndex] = project;
      return next;
    });
    setUpdatedAt(Date.now());
  }, []);

  const remove = useCallback((id: string) => {
    pendingRemovedProjectIdsRef.current.add(id);
    pendingProjectsRef.current = pendingProjectsRef.current.filter((project) => project.id !== id);
    setProjects((current) => current.filter((project) => project.id !== id));
    setUpdatedAt(Date.now());
  }, []);

  const list = useCallback(
    async (limit = PROJECTS_PAGE_SIZE, before: string | null = null): Promise<Project[] | null> => {
      const requestedScope = cacheScope();
      const requestId = ++listRequestIdRef.current;
      const isFirstLoad = !projectsLoadedRef.current;
      if (isFirstLoad) setLoading(true);
      setError(null);
      setListError(null);

      try {
        const params = new URLSearchParams();
        params.set("limit", String(limit));
        if (before) params.set("before", before);

        const res = await fetchWithAuth(`${API_BASE}?${params}`, { headers: {} }, getToken);
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            clearCacheOnAuthFailure();
            setProjects([]); setLoaded(false); projectsLoadedRef.current = false; setLiveConfirmedProjects([]);
            const message = `HTTP ${res.status}: ${res.statusText}`;
            setError(message); setListError(message);
            void invalidateCache("projectLists"); void invalidateCache("contextDetails", "project"); void invalidateCache("taskPages");
          }
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const data: ProjectListResponse = await res.json();
        if (requestedScope !== cacheScope() || requestId !== listRequestIdRef.current) return null;
        if (!before) setLiveConfirmedProjects(data.projects.map((project) => project.id));
        if (!before && limit === PROJECTS_PAGE_SIZE) {
          const safe = data.projects.map(projectPreview).filter((row): row is CachedProject => !!row);
          void writeCache("projectLists", "sidebar", "first", { projects: safe, has_more: data.has_more === true, next_cursor: data.next_cursor ?? null });
        }
        setProjects((current) => {
          if (!before) {
            const serverProjectIds = new Set(data.projects.map((project) => project.id));
            // Keep a confirmed local archive hidden while an older in-flight
            // list response still contains it. Once the API response no
            // longer includes the id, future lists can rely on server truth.
            for (const id of pendingRemovedProjectIdsRef.current) {
              if (!serverProjectIds.has(id)) pendingRemovedProjectIdsRef.current.delete(id);
            }
            const serverProjects = data.projects.filter(
              (project) => !pendingRemovedProjectIdsRef.current.has(project.id),
            );
            const serverIds = new Set(serverProjects.map((project) => project.id));
            const pending = pendingProjectsRef.current.filter((project) => !serverIds.has(project.id));
            pendingProjectsRef.current = pending;
            return pending.length > 0 ? [...pending, ...serverProjects] : serverProjects;
          }

          const knownIds = new Set(current.map((project) => project.id));
          return [...current, ...data.projects.filter((project) => !knownIds.has(project.id))];
        });
        setHasMore(data.has_more ?? false);
        setNextCursor(data.next_cursor ?? null);
        projectsLoadedRef.current = true;
        setLoaded(true);
        setUpdatedAt(Date.now());
        return data.projects;
      } catch (err: unknown) {
        if (requestedScope !== cacheScope() || requestId !== listRequestIdRef.current) return null;
        setLiveConfirmedProjects([]);
        const message = err instanceof Error ? err.message : i18n.t("projects:catalog.loadFailed");
        setError(message);
        setListError(message);
        return null;
      } finally {
        if (isFirstLoad) setLoading(false);
      }
    },
    [getToken],
  );

  const create = useCallback(
    async (input: ProjectCreateRequest): Promise<Project | null> => {
      setError(null);
      try {
        const res = await fetchWithAuth(
          API_BASE,
          { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        const project: Project = await res.json();
        confirmProjectDetail(project.id);
        upsert(project);
        void invalidateCache("projectLists"); void invalidateCache("contextDetails", "project"); void invalidateCache("taskPages");
        return project;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : i18n.t("projects:projectCreateFailed"));
        return null;
      }
    },
    [getToken, upsert],
  );

  const get = useCallback(
    async (id: string): Promise<Project | null> => {
      setError(null);
      try {
        const res = await fetchWithAuth(
          `${API_BASE}/${encodeURIComponent(id)}`,
          { headers: {} },
          getToken,
        );
        if (!res.ok) {
          if (res.status === 404) void invalidateCache("contextDetails", "project", id);
          if (res.status === 401 || res.status === 403) { clearCacheOnAuthFailure(); setProjects([]); void invalidateCache("projectLists"); void invalidateCache("taskPages"); }
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const project: Project = await res.json();
        confirmProjectDetail(project.id);
        const safe = projectDetailPreview(project);
        if (safe) void writeCache("contextDetails", "project", id, safe);
        return project;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : i18n.t("projects:projectLoadFailed"));
        return null;
      }
    },
    [getToken],
  );

  const update = useCallback(
    async (id: string, input: ProjectUpdateRequest): Promise<Project | null> => {
      setError(null);
      try {
        const res = await fetchWithAuth(
          `${API_BASE}/${encodeURIComponent(id)}`,
          { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(input) },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        const project: Project = await res.json();
        upsert(project);
        void invalidateCache("projectLists"); void invalidateCache("contextDetails", "project", id); void invalidateCache("taskPages");
        return project;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : i18n.t("projects:projectUpdateFailed"));
        return null;
      }
    },
    [getToken, upsert],
  );

  const archive = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);
      try {
        const res = await fetchWithAuth(
          `${API_BASE}/${encodeURIComponent(id)}`,
          { method: "DELETE", headers: {} },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        remove(id);
        void invalidateCache("projectLists"); void invalidateCache("contextDetails", "project", id); void invalidateCache("taskPages");
        return true;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : i18n.t("projects:projectArchiveFailed"));
        return false;
      }
    },
    [getToken, remove],
  );

  const setPinned = useCallback(
    async (id: string, isPinned: boolean): Promise<boolean> => {
      setError(null);
      try {
        const res = await fetchWithAuth(
          `${API_BASE}/${encodeURIComponent(id)}/pin`,
          { method: isPinned ? "POST" : "DELETE", headers: isPinned ? jsonHeaders : {} },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        setProjects((current) => {
          const nextProject = current.find((project) => project.id === id);
          if (!nextProject) return current;
          const updatedProject = { ...nextProject, is_pinned: isPinned };
          pendingProjectsRef.current = [
            updatedProject,
            ...pendingProjectsRef.current.filter((project) => project.id !== id),
          ];
          return current.map((project) => (project.id === id ? updatedProject : project));
        });
        setUpdatedAt(Date.now());
        void invalidateCache("projectLists"); void invalidateCache("contextDetails", "project", id); void invalidateCache("taskPages");
        return true;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : i18n.t("projects:projectPinUpdateFailed"));
        return false;
      }
    },
    [getToken],
  );

  const pin = useCallback((id: string) => setPinned(id, true), [setPinned]);
  const unpin = useCallback((id: string) => setPinned(id, false), [setPinned]);

  const refresh = useCallback((): Promise<Project[] | null> => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const request = list();
    refreshInFlightRef.current = request;
    void request.finally(() => {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
    });
    return request;
  }, [list]);

  const revalidateAccess = useCallback((): Promise<Project[] | null> => {
    // Keep the last confirmed list visible while checking access again.
    // list() replaces it with the server response or clears it on 401/403.
    refreshInFlightRef.current = null;
    return list();
  }, [list]);

  useRefreshOnReconnect(refresh, autoLoad);

  useEffect(() => {
    if (!autoLoad) return;
    const onVisibility = () => { if (document.visibilityState === "visible") void revalidateAccess(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [autoLoad, revalidateAccess]);

  useEffect(() => {
    if (!autoLoad) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [autoLoad, refresh]);

  useEffect(() => {
    if (previousScopeRef.current === verifiedScope) return;
    previousScopeRef.current = verifiedScope;
    setProjects([]); setLoaded(false); projectsLoadedRef.current = false;
    listRequestIdRef.current += 1;
    refreshInFlightRef.current = null;
    if (!verifiedScope) return;
    if (autoLoad) void revalidateAccess();
  }, [verifiedScope, autoLoad, revalidateAccess]);

  return {
    projects,
    loading,
    error,
    loaded,
    updatedAt,
    listError,
    hasMore,
    nextCursor,
    list,
    create,
    get,
    update,
    archive,
    pin,
    unpin,
    upsert,
    remove,
    refresh,
    revalidateAccess,
  };
}
